import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorkOrder, Client, CompanySettings, Technician, UserRole } from '../types';
import { generateServiceReport, generateSummaryReport } from '../utils/pdfHelper';

interface useAppHandlersProps {
    orders: WorkOrder[];
    technicians: Technician[];
    companySettings: CompanySettings;
    deleteOrder: (id: string) => Promise<void>;
    deleteClient: (id: string) => Promise<void>;
    updateOrder?: (order: WorkOrder) => void;
    showToast: (msg: string, type: 'success' | 'error' | 'info') => void;
    confirm: (options: { title: string; message: string }) => Promise<boolean>;
    login: (role: UserRole) => void;
}

export const useAppHandlers = ({
    orders,
    technicians,
    companySettings,
    deleteOrder,
    deleteClient,
    updateOrder,
    showToast,
    confirm,
    login
}: useAppHandlersProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleLogin = useCallback((role: UserRole) => {
        login(role);
        setTimeout(() => {
            if (role === 'technician') {
                navigate('/orders', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }, 0);
    }, [login, navigate]);

    const handleDeleteOrder = useCallback((orderId: string) => {
        deleteOrder(orderId);
    }, [deleteOrder]);

    const handleDeleteOrders = useCallback(async (orderIds: string[]) => {
        await Promise.all(orderIds.map(id => deleteOrder(id)));
    }, [deleteOrder]);

    const handleGeneratePDF = useCallback(async (client: Client, selectedIds: string[], dateStart?: string, dateEnd?: string) => {
        const clientOrders = (orders || []).filter(o => selectedIds.includes(o.id));
        if (clientOrders.length === 0) {
            showToast(t('toast.orders.selectWork'), 'info');
            return;
        }

        const exportedAt = new Date().toISOString();

        // Marcar jornadas exportadas con sello lastExportedAt
        const markExported = (order: WorkOrder): WorkOrder => {
            const updatedJournal = (order.laborJournal || []).map(entry => {
                const entryDate = entry.date;
                const afterStart = !dateStart || entryDate >= dateStart;
                const beforeEnd  = !dateEnd   || entryDate <= dateEnd;
                if (afterStart && beforeEnd) {
                    return { ...entry, lastExportedAt: exportedAt };
                }
                return entry;
            });
            return { ...order, laborJournal: updatedJournal, lastExportedAt: exportedAt };
        };

        try {
            const dateRange = (dateStart || dateEnd) ? { start: dateStart, end: dateEnd } : undefined;

            if (clientOrders.length === 1) {
                const order = clientOrders[0];
                const tech = technicians.find(t => t.id === order.technicianId);
                const techName = tech ? tech.name : (order.technicianNameSnapshot || "Desconocido");
                await generateServiceReport(order, techName, companySettings.name, companySettings.companyLogo || undefined, dateRange);
            } else {
                await generateSummaryReport(clientOrders, client.name, companySettings.name, companySettings.companyLogo || undefined, dateRange);
            }

            // Guardar el sello de exportación en cada parte seleccionado
            if (updateOrder) {
                clientOrders.forEach(order => {
                    updateOrder(markExported(order));
                });
            }

            showToast(t('toast.orders.reportGenerated'), 'success');
        } catch (e) {
            console.error(e);
            showToast(t('toast.orders.pdfError'), 'error');
        }
    }, [orders, technicians, companySettings.name, companySettings.companyLogo, updateOrder, showToast]);

    const handleRequestDeleteClient = useCallback(async (clientId: string) => {
        // V228: Verificación de seguridad. No permitimos borrar clientes con historial de partes.
        const clientOrders = (orders || []).filter(o => o.clientId === clientId);
        
        if (clientOrders.length > 0) {
            showToast(`No se puede eliminar: el cliente tiene ${clientOrders.length} partes de trabajo vinculados. Debes eliminarlos primero.`, 'error');
            return;
        }

        const ok = await confirm({
            title: '¿Eliminar cliente?',
            message: 'Esta acción es irreversible. El cliente y sus ubicaciones se borrarán permanentemente.'
        });
        if (ok) deleteClient(clientId);
    }, [confirm, deleteClient, orders, showToast]);

    return {
        handleLogin,
        handleDeleteOrder,
        handleDeleteOrders,
        handleGeneratePDF,
        handleRequestDeleteClient
    };
};
